import React from 'react';
import { Icon } from '@iconify/react';
import { LoyaltyProgram } from '@/types/loyalty';

interface Props {
  formData: LoyaltyProgram;
  onChange: (patch: Partial<LoyaltyProgram>) => void;
  arenaFields?: { id: number; name: string }[];
  disabled?: boolean;
}

export const StampSettingsForm: React.FC<Props> = ({
  formData,
  onChange,
  arenaFields = [],
  disabled = false,
}) => {
  const targetCount = formData.stamp_target_count || 10;
  const rewardHours = formData.stamp_reward_free_hours || 1;
  const minDuration = formData.stamp_min_booking_duration || 1;
  const expiryMonths = formData.stamp_expiry_months || 6;

  const selectedIds = formData.stamp_applicable_field_ids || [];

  const isFieldChecked = (fieldId: number) => selectedIds.includes(fieldId);

  const toggleField = (fieldId: number) => {
    if (selectedIds.includes(fieldId)) {
      // Keep at least 1 field selected
      if (selectedIds.length <= 1) return;
      onChange({
        stamp_applicable_field_ids: selectedIds.filter((id) => id !== fieldId),
      });
    } else {
      onChange({
        stamp_applicable_field_ids: [...selectedIds, fieldId],
      });
    }
  };

  return (
    <div className="space-y-5">
      {/* Row 1: Jumlah booking & Hadiah gratis */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div>
            <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Jumlah booking
            </label>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Yang harus dikumpulkan
            </p>
          </div>
          <div className="flex items-center justify-between h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden">
            <button
              type="button"
              disabled={disabled || targetCount <= 2}
              onClick={() =>
                onChange({ stamp_target_count: Math.max(2, targetCount - 1) })
              }
              className="w-10 h-full border-r border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
            >
              <Icon icon="ph:minus" className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              {targetCount} kali
            </span>
            <button
              type="button"
              disabled={disabled || targetCount >= 50}
              onClick={() =>
                onChange({ stamp_target_count: Math.min(50, targetCount + 1) })
              }
              className="w-10 h-full border-l border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
            >
              <Icon icon="ph:plus" className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <div>
            <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Hadiah gratis
            </label>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Setelah target tercapai
            </p>
          </div>
          <div className="flex items-center justify-between h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden">
            <button
              type="button"
              disabled={disabled || rewardHours <= 1}
              onClick={() =>
                onChange({ stamp_reward_free_hours: Math.max(1, rewardHours - 1) })
              }
              className="w-10 h-full border-r border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
            >
              <Icon icon="ph:minus" className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              {rewardHours} jam
            </span>
            <button
              type="button"
              disabled={disabled || rewardHours >= 3}
              onClick={() =>
                onChange({ stamp_reward_free_hours: Math.min(3, rewardHours + 1) })
              }
              className="w-10 h-full border-l border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
            >
              <Icon icon="ph:plus" className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Row 2: Minimum durasi */}
      <div className="space-y-1.5">
        <div>
          <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
            Minimum durasi
          </label>
          <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
            Booking di bawah ini tidak dihitung
          </p>
        </div>
        <div className="flex items-center justify-between h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden">
          <button
            type="button"
            disabled={disabled || minDuration <= 1}
            onClick={() =>
              onChange({ stamp_min_booking_duration: Math.max(1, minDuration - 1) })
            }
            className="w-10 h-full border-r border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
          >
            <Icon icon="ph:minus" className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs sm:text-[13px] font-semibold text-[#0E1726]">
            {minDuration} jam
          </span>
          <button
            type="button"
            disabled={disabled || minDuration >= 24}
            onClick={() =>
              onChange({ stamp_min_booking_duration: Math.min(24, minDuration + 1) })
            }
            className="w-10 h-full border-l border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
          >
            <Icon icon="ph:plus" className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Row 3: Berlaku di lapangan */}
      <div className="space-y-2">
        <div>
          <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
            Berlaku di lapangan
          </label>
          <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
            Booking dan hadiah hanya untuk lapangan terpilih
          </p>
        </div>

        {arenaFields.length === 0 ? (
          <p className="text-xs text-[#8C8C8C] pt-1">
            Berlaku otomatis untuk seluruh lapangan arena.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            {arenaFields.map((field) => {
              const checked = isFieldChecked(field.id);
              return (
                <button
                  key={field.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleField(field.id)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-xs transition-all cursor-pointer ${
                    checked
                      ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D] font-semibold'
                      : 'border-[#E0E6ED] bg-white text-[#8C8C8C] hover:text-[#0E1726] hover:bg-gray-50 font-medium'
                  } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  {checked && <Icon icon="ph:check-bold" className="w-3 h-3" />}
                  <span>{field.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Divider + Batas waktu kumpul */}
      <div className="border-t border-[#F0F2F5] pt-5 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <span className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
              Batas waktu kumpul
            </span>
            <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
              Hitungan reset jika target tidak tercapai
            </p>
          </div>
          <label
            className={`relative inline-flex items-center ${
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            }`}
          >
            <input
              type="checkbox"
              disabled={disabled}
              checked={formData.stamp_has_expiry}
              onChange={(e) => onChange({ stamp_has_expiry: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-10 h-5 bg-[#E0E6ED] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FC480D]" />
          </label>
        </div>

        {formData.stamp_has_expiry && (
          <div className="space-y-1.5">
            <div>
              <label className="block text-xs sm:text-[13px] font-semibold text-[#0E1726]">
                Periode
              </label>
              <p className="text-[11px] sm:text-xs text-[#8C8C8C] mt-0.5">
                Sejak booking pertama dihitung
              </p>
            </div>

            <div className="flex items-center justify-between h-10 rounded-xl border border-[#E0E6ED] bg-white overflow-hidden">
              <button
                type="button"
                disabled={disabled || expiryMonths <= 1}
                onClick={() =>
                  onChange({ stamp_expiry_months: Math.max(1, expiryMonths - 1) })
                }
                className="w-10 h-full border-r border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
              >
                <Icon icon="ph:minus" className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs sm:text-[13px] font-semibold text-[#0E1726]">
                {expiryMonths} bulan
              </span>
              <button
                type="button"
                disabled={disabled || expiryMonths >= 24}
                onClick={() =>
                  onChange({ stamp_expiry_months: Math.min(24, expiryMonths + 1) })
                }
                className="w-10 h-full border-l border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-[#F8F9FA] disabled:text-gray-300 disabled:hover:bg-white transition-colors cursor-pointer disabled:cursor-not-allowed"
              >
                <Icon icon="ph:plus" className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StampSettingsForm;
